/**
 * Language.
 *
 * The reason this exists is not reach, it is consent. Ori asks people to donate
 * a reading of their own body to a research corpus. Consent given in a language
 * you do not read is not consent, it is a signature. So the strings that are
 * translated first are the ones that carry an obligation: what this is, what is
 * being asked, what happens to it, and how to take it back.
 *
 * Scope, stated honestly rather than implied: the participant journey — the
 * opener, the scan, the guidance, the result, and every consent switch — is
 * translated. The operator console is not; it is an internal tool used by
 * OmniAlera staff, and pretending otherwise would spread the translation effort
 * thinner than the consent copy deserves.
 *
 * The Yoruba, Hausa and Igbo strings are a first pass and are marked in the
 * locale files as awaiting native clinical review. They ship because a
 * first-pass translation a person can read beats flawless English they cannot —
 * but they are not to be treated as final, and `reviewed: false` is the flag a
 * release process should be gating on.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.js';
import yo from '../locales/yo.js';
import ha from '../locales/ha.js';
import ig from '../locales/ig.js';

export const LOCALES = Object.freeze([en, yo, ha, ig]);
const BY_CODE = Object.fromEntries(LOCALES.map((l) => [l.code, l]));

const STORAGE_KEY = 'omnialera.ori.locale';

function detect() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && BY_CODE[saved]) return saved;
  } catch {
    /* private mode */
  }
  for (const tag of navigator.languages ?? [navigator.language ?? 'en']) {
    const base = String(tag).toLowerCase().split('-')[0];
    if (BY_CODE[base]) return base;
  }
  return 'en';
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [code, setCode] = useState(detect);

  useEffect(() => {
    document.documentElement.lang = code;
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* private mode */
    }
  }, [code]);

  const value = useMemo(() => {
    const locale = BY_CODE[code] ?? en;
    /**
     * Look up a string, falling back to English rather than showing a key.
     * A missing translation should read as untranslated, never as broken.
     */
    const t = (key, vars) => {
      const raw = locale.strings[key] ?? en.strings[key];
      if (raw === undefined) {
        if (import.meta.env?.DEV) console.warn(`[i18n] missing string: ${key}`);
        return key;
      }
      if (!vars) return raw;
      return raw.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
    };
    return { code, setCode, locale, t, locales: LOCALES };
  }, [code]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** The common case: just the lookup function. */
export function useT() {
  return useI18n().t;
}

/** Format a number in the reader's locale, keeping digits legible on small screens. */
export function useNumber() {
  const { locale } = useI18n();
  return useCallback(
    (value, options) => new Intl.NumberFormat(locale.intl ?? 'en', options).format(value),
    [locale],
  );
}
